/**
 * ClientForm Component
 * Reusable form component for creating and editing RevFinOps clients
 * Includes comprehensive validation for all fields
 */

import { useState, useEffect, FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { RevFinOpsClient } from '@/lib/revfinops-api'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Props for ClientForm component
 */
interface ClientFormProps {
  mode: 'create' | 'edit'
  initialData?: Partial<RevFinOpsClient>
  onSubmit: (data: ClientFormData) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

/**
 * Form data structure
 */
export interface ClientFormData {
  name: string
  status: 'active' | 'inactive' | 'pending'
  contactEmail: string
  contactPhone?: string
  notes?: string
}

/**
 * Form validation errors
 */
interface FormErrors {
  name?: string
  status?: string
  contactEmail?: string
  contactPhone?: string
}

/**
 * Field touched state (for showing errors only after blur)
 */
interface TouchedFields {
  name?: boolean
  status?: boolean
  contactEmail?: boolean
  contactPhone?: boolean
  notes?: boolean
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate a single form field
 * @param field - Field name to validate
 * @param value - Field value
 * @returns Error message or undefined if valid
 */
function validateField(field: keyof ClientFormData, value: string | undefined): string | undefined {
  switch (field) {
    case 'name': {
      if (!value || value.trim() === '') {
        return 'Client name is required'
      }
      if (value.trim().length < 2) {
        return 'Client name must be at least 2 characters'
      }
      break
    }

    case 'contactEmail': {
      if (!value || value.trim() === '') {
        return 'Contact email is required'
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address'
      }
      break
    }

    case 'contactPhone': {
      if (value && value.trim() !== '') {
        const phoneRegex = /^\(\d{2}\) \d{4,5}-\d{4}$/
        if (!phoneRegex.test(value)) {
          return 'Phone must be in format: (XX) XXXXX-XXXX'
        }
      }
      break
    }
  }

  return undefined
}

/**
 * Validate entire form
 * @param data - Form data to validate
 * @returns Object containing all validation errors
 */
function validateForm(data: ClientFormData): FormErrors {
  const errors: FormErrors = {}

  const nameError = validateField('name', data.name)
  if (nameError) errors.name = nameError

  const emailError = validateField('contactEmail', data.contactEmail)
  if (emailError) errors.contactEmail = emailError

  const phoneError = validateField('contactPhone', data.contactPhone)
  if (phoneError) errors.contactPhone = phoneError

  return errors
}

/**
 * Format phone number to BR format with mask
 * Supports both mobile (11 digits) and landline (10 digits)
 * @param value - Raw input value
 * @returns Formatted phone number
 */
function formatPhone(value: string): string {
  // Remove all non-digits
  const cleaned = value.replace(/\D/g, '')

  // Apply mask based on length
  if (cleaned.length === 0) {
    return ''
  } else if (cleaned.length <= 2) {
    return cleaned
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`
  } else {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ClientForm - Reusable form for creating and editing RevFinOps clients
 *
 * Features:
 * - Create and edit modes
 * - Comprehensive field validation
 * - Real-time validation feedback
 * - BR phone number formatting
 * - Loading state management
 * - Disabled state when invalid
 * - Touch-based error display (errors show only after field is touched)
 *
 * @param mode - 'create' or 'edit' mode
 * @param initialData - Initial form data (used in edit mode)
 * @param onSubmit - Callback function when form is submitted
 * @param onCancel - Callback function when cancel is clicked
 * @param loading - Loading state flag
 */
export function ClientForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  loading = false
}: ClientFormProps) {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [formData, setFormData] = useState<ClientFormData>({
    name: initialData?.name || '',
    status: initialData?.status || 'active',
    contactEmail: initialData?.email || '',
    contactPhone: initialData?.phone || '',
    notes: initialData?.notes || ''
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<TouchedFields>({})

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        status: initialData.status || 'active',
        contactEmail: initialData.email || '',
        contactPhone: initialData.phone || '',
        notes: initialData.notes || ''
      })
    }
  }, [initialData])

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle field blur - marks field as touched and validates
   */
  const handleBlur = (field: keyof ClientFormData) => {
    setTouched({ ...touched, [field]: true })

    // Validate on blur
    const fieldError = validateField(field, formData[field])
    setErrors({ ...errors, [field]: fieldError })
  }

  /**
   * Handle field change - updates form data and clears errors if field becomes valid
   */
  const handleChange = (field: keyof ClientFormData, value: string) => {
    const newFormData = { ...formData, [field]: value }
    setFormData(newFormData)

    // Clear error if field becomes valid
    if (touched[field]) {
      const fieldError = validateField(field, value)
      setErrors({ ...errors, [field]: fieldError })
    }
  }

  /**
   * Handle phone input change with formatting
   */
  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value)
    handleChange('contactPhone', formatted)
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Mark all fields as touched
    setTouched({
      name: true,
      status: true,
      contactEmail: true,
      contactPhone: true
    })

    // Validate entire form
    const validationErrors = validateForm(formData)
    setErrors(validationErrors)

    // Don't submit if there are errors
    if (Object.keys(validationErrors).length > 0) {
      return
    }

    // Submit form data
    await onSubmit(formData)
  }

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Check if form is valid (all required fields filled, no errors)
   */
  const isFormValid =
    Object.keys(errors).length === 0 &&
    formData.name.trim() !== '' &&
    formData.contactEmail.trim() !== ''

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Client Name */}
      <div className="form-group">
        <label className="form-label form-label-required">Client Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          onBlur={() => handleBlur('name')}
          disabled={loading}
          className={`form-input ${touched.name && errors.name ? 'form-input-error' : ''}`}
          placeholder="Enter client name"
          aria-invalid={touched.name && !!errors.name}
          aria-describedby={touched.name && errors.name ? 'name-error' : undefined}
        />
        {touched.name && errors.name && (
          <p id="name-error" className="form-error" role="alert">
            {errors.name}
          </p>
        )}
      </div>

      {/* Status */}
      <div className="form-group">
        <label className="form-label form-label-required">Status</label>
        <select
          value={formData.status}
          onChange={(e) =>
            handleChange('status', e.target.value as 'active' | 'inactive' | 'pending')
          }
          onBlur={() => handleBlur('status')}
          disabled={loading}
          className="form-select"
          aria-invalid={touched.status && !!errors.status}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
        {touched.status && errors.status && (
          <p className="form-error" role="alert">
            {errors.status}
          </p>
        )}
      </div>

      {/* Contact Email */}
      <div className="form-group">
        <label className="form-label form-label-required">Contact Email</label>
        <input
          type="email"
          value={formData.contactEmail}
          onChange={(e) => handleChange('contactEmail', e.target.value)}
          onBlur={() => handleBlur('contactEmail')}
          disabled={loading}
          className={`form-input ${
            touched.contactEmail && errors.contactEmail ? 'form-input-error' : ''
          }`}
          placeholder="email@example.com"
          aria-invalid={touched.contactEmail && !!errors.contactEmail}
          aria-describedby={touched.contactEmail && errors.contactEmail ? 'email-error' : undefined}
        />
        {touched.contactEmail && errors.contactEmail && (
          <p id="email-error" className="form-error" role="alert">
            {errors.contactEmail}
          </p>
        )}
      </div>

      {/* Contact Phone */}
      <div className="form-group">
        <label className="form-label">Contact Phone</label>
        <input
          type="tel"
          value={formData.contactPhone || ''}
          onChange={(e) => handlePhoneChange(e.target.value)}
          onBlur={() => handleBlur('contactPhone')}
          disabled={loading}
          className={`form-input ${
            touched.contactPhone && errors.contactPhone ? 'form-input-error' : ''
          }`}
          placeholder="(11) 98765-4321"
          aria-invalid={touched.contactPhone && !!errors.contactPhone}
          aria-describedby="phone-help"
        />
        <p id="phone-help" className="form-help">
          Format: (XX) XXXXX-XXXX or (XX) XXXX-XXXX
        </p>
        {touched.contactPhone && errors.contactPhone && (
          <p className="form-error" role="alert">
            {errors.contactPhone}
          </p>
        )}
      </div>

      {/* Notes */}
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          disabled={loading}
          className="form-textarea"
          placeholder="Additional notes about this client..."
          rows={4}
          aria-describedby="notes-help"
        />
        <p id="notes-help" className="form-help">
          Optional additional information about the client
        </p>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="btn-ghost"
          aria-label="Cancel form"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!isFormValid || loading}
          className="btn-primary flex items-center gap-2"
          aria-label={mode === 'create' ? 'Create client' : 'Save changes'}
        >
          {loading && <Loader2 className="animate-spin" size={18} />}
          {loading ? 'Saving...' : mode === 'create' ? 'Create Client' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

export default ClientForm
